import * as React from "react";
import classnames from "classnames";
import DatePicker from "react-datepicker";
import { useEffect, useState } from "react";
import { Checkbox } from "../Elements/Checkbox";
import { IInputFieldConfig } from "../Map/config";
import { Input } from "../Map/enums";
import { InfoButton } from "../Popups/Info/InfoButton";
import { useRegistrationContext } from "../Contexts/RegistrationContext";
import { isWorkingDay } from "../Map/utils";
import "react-datepicker/dist/react-datepicker.css";

export interface IInputField {
    config: IInputFieldConfig;
    onChange: CallableFunction;
    validate: number;
    onValidate: (isValid: boolean) => void;
    value?: any;
}

export const InputField = ({ config, onChange, validate, onValidate, value }: IInputField): JSX.Element => {
    const [valid, setValid] = useState<boolean>(true);
    const { rawData } = useRegistrationContext();

    const changeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isLengthExceeded = e.target.value.length > config.maxlength;
        setValid(!isLengthExceeded);
        !isLengthExceeded && onChange(e);
    };

    useEffect(() => {
        if (validate === 0) {
            setValid(false);
        } else if (validate === 1) {
            setValid(true);
        } else {
            const isValid =
                !config.required && !value
                    ? true
                    : config.required && !value
                    ? false
                    : config.validationFn
                    ? config.validationFn(
                          value,
                          config.validationComparator &&
                              rawData[config.validationComparator] &&
                              rawData[config.validationComparator].toString()
                      )
                    : true;
            setValid(isValid);
            onValidate(isValid);
        }
    }, [validate, value]);

    return (
        <div
            className={classnames("form-field", {
                invalid: !valid || config.hasError,
                "checkbox-input": config.type === Input.Checkbox
            })}
        >
            <div className="field-input">
                {config.type === Input.Checkbox ? (
                    <InputCheckbox config={config} value={!!value} changeValue={changeValue}></InputCheckbox>
                ) : config.type === Input.Select ? (
                    <InputSelect config={config} value={value} changeValue={changeValue}></InputSelect>
                ) : config.type === Input.TextArea ? (
                    <InputTextArea config={config} value={value} changeValue={changeValue}></InputTextArea>
                ) : config.type === Input.Date ? (
                    <InputDate config={config} value={value} changeValue={changeValue}></InputDate>
                ) : (
                    <InputText config={config} value={value} changeValue={changeValue}></InputText>
                )}
            </div>
            {!valid || config.hasError ? (
                <span className="field-validation">{config.validationErrorMessage || "Kļūda!"}</span>
            ) : null}
        </div>
    );
};

const InputSelect = ({
    config,
    value,
    changeValue
}: {
    config: IInputFieldConfig;
    value: string;
    changeValue: CallableFunction;
}) => {
    const { options } = useRegistrationContext();

    return (
        <>
            <label className={classnames("input-label", { required: config.required })}>
                {config.title}
                {config.info && <InfoButton text={config.info}></InfoButton>}
            </label>
            <div className="select input-element">
                <select
                    className="sv-select"
                    name={config.field}
                    value={value}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => changeValue(e)}
                >
                    {options &&
                        options[config.field] &&
                        options[config.field].map((cv) => (
                            <option key={cv.name + "-option"} value={cv.code}>
                                {cv.name}
                            </option>
                        ))}
                </select>
            </div>
        </>
    );
};

const InputCheckbox = ({
    config,
    value,
    changeValue
}: {
    config: IInputFieldConfig;
    value: boolean;
    changeValue: CallableFunction;
}) => (
    <>
        <label className={classnames("input-label", { required: config.required })}>
            {config.title}
            {config.info && <InfoButton text={config.info}></InfoButton>}
        </label>
        <Checkbox
            key={config.field + "checkbox"}
            checked={value}
            onChange={(checked) =>
                changeValue(({
                    target: {
                        value: checked,
                        name: config.field
                    }
                } as unknown) as React.ChangeEvent<HTMLInputElement>)
            }
        ></Checkbox>
    </>
);

const InputText = ({
    config,
    value,
    changeValue
}: {
    config: IInputFieldConfig;
    value: string;
    changeValue: CallableFunction;
}) => (
    <>
        <label className={classnames("input-label", { required: config.required })}>
            {config.title}
            {config.info && <InfoButton text={config.info}></InfoButton>}
        </label>
        <input
            className="input-element"
            id={config.field}
            name={config.field}
            type={config.type}
            autoComplete={config.autocomplete || "off"}
            required={config.required || false}
            value={value}
            min={config.minDate}
            max={config.maxDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => changeValue(e)}
        />
    </>
);

const InputTextArea = ({
    config,
    value,
    changeValue
}: {
    config: IInputFieldConfig;
    value: string;
    changeValue: CallableFunction;
}) => (
    <>
        <label className={classnames("input-label", { required: config.required })}>
            {config.title}
            {config.info && <InfoButton text={config.info}></InfoButton>}
        </label>
        <textarea
            className="input-element"
            id={config.field}
            name={config.field}
            autoComplete={config.autocomplete || "off"}
            required={config.required || false}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => changeValue(e)}
        />
    </>
);

const InputDate = ({
    config,
    value,
    changeValue
}: {
    config: IInputFieldConfig;
    value: string;
    changeValue: CallableFunction;
}) => {
    const onDateChange = (date: Date) => {
        const formated = date
            ? date.getFullYear() +
              "-" +
              (date.getMonth() + 1).toString().padStart(2, "0") +
              "-" +
              date.getDate().toString().padStart(2, "0")
            : null;

        changeValue({ target: { name: config.field, value: formated } });
    };

    return (
        <>
            <label className={classnames("input-label", { required: config.required })}>
                {config.title}
                {config.info && <InfoButton text={config.info}></InfoButton>}
            </label>
            <div className="input-element datepicker">
                <DatePicker
                    className="date-input"
                    id={config.field}
                    calendarStartDay={1}
                    dateFormat="dd/MM/yyyy"
                    autoComplete="off"
                    selected={Date.parse(value)}
                    minDate={Date.parse(config.minDate)}
                    maxDate={Date.parse(config.maxDate)}
                    onChange={onDateChange}
                    onFocus={(e) => e.target.blur()}
                    popperModifiers={[
                        {
                            name: "preventOverflow",
                            options: {
                                rootBoundary: "viewport",
                                tether: false,
                                altAxis: true,
                                padding: { bottom: 100 }
                            }
                        }
                    ]}
                />
            </div>
        </>
    );
};
